import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { LogIn } from "lucide-react";
import { useAuthContext } from "@/contexts/auth";
import { UserAvatarDropdown } from "@/components/UserAvatarDropdown";
import { useHydrationSafe } from "@/hooks/useHydrationSafe";
import { cn } from "@/lib/utils";

interface AuthButtonsProps {
  onNavigate?: () => void;
  variant?: 'horizontal' | 'vertical';
}

export function AuthButtons({ onNavigate, variant = 'horizontal' }: AuthButtonsProps) {
  const { isAuthenticated } = useAuthContext();
  const { isHydrated } = useHydrationSafe();

  // During SSR and first client render: always show login button (default state)
  // This ensures SSR output matches first client render, preventing hydration errors
  // CRITICAL: Use static Link with button classes - NO Button asChild (Radix Slot causes #418/#422)
  if (!isHydrated) {
    return (
      <Link 
        to="/auth/login"
        className={cn(
          // buttonVariants size="sm" variant="default" - copied to avoid Radix Slot
          "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium",
          "ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2",
          "focus-visible:ring-ring focus-visible:ring-offset-2",
          "bg-primary text-primary-foreground hover:bg-primary/90",
          "h-9 px-3",
          "[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
          variant === 'vertical' && "mt-4"
        )}
      >
        <LogIn className="h-4 w-4 mr-2" />
        Accedi
      </Link>
    );
  }

  // After hydration: use actual auth state
  if (isAuthenticated) {
    return (
      <UserAvatarDropdown onNavigate={onNavigate} />
    );
  }

  return (
    <Button 
      asChild 
      size="sm"
      className={variant === 'vertical' ? "mt-4" : ""}
    >
      <Link to="/auth/login" onClick={typeof onNavigate === 'function' ? onNavigate : undefined}>
        <LogIn className="h-4 w-4 mr-2" />
        Accedi
      </Link>
    </Button>
  );
}
